import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Target } from "lucide-react";
import { ModernPagination } from "@/components/admin/ModernPagination";
import { useOpenOneThingChat } from "@/components/copilot/use-open-onething-chat";
import { PageShell } from "@/components/layout/PageShell";
import { PageHeaderFrame } from "@/components/layout/page-header-frame";
import { canAccessPriority } from "@/components/priorities/priority-access";
import { PriorityFilters } from "@/components/priorities/PriorityFilters";
import { PriorityDetailDialog } from "@/components/priorities/PriorityDetailDialog";
import { PriorityDeleteDialog } from "@/components/priorities/PriorityDeleteDialog";
import { PriorityList } from "@/components/priorities/PriorityList";
import { PriorityStats } from "@/components/priorities/PriorityStats";
import { CandidateSection } from "@/components/priorities/CandidateSection";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";
import { useDebounce } from "@/hooks/useDebounce";
import { PRIORITY_ITEMS_PER_PAGE, isHidingDone, usePriorityListFilters } from "@/hooks/usePriorityListFilters";
import { usePriorityPageData } from "@/hooks/usePriorityPageData";
import { Priority } from "@/types";

const Priorities = () => {
  const { user, isAdmin, canViewOkr } = useAuth();
  const navigate = useNavigate();
  const filters = usePriorityListFilters(user?.pid);
  const [userSearch, setUserSearch] = useState("");
  const debouncedUserSearch = useDebounce(userSearch, 300);
  const openChat = useOpenOneThingChat();

  const [isDataDialogOpen, setIsDataDialogOpen] = useState(false);
  // A seed for the dialog, which re-reads the row from the server itself.
  const [editingPriority, setEditingPriority] = useState<Priority | null>(null);
  const [priorityToDelete, setPriorityToDelete] = useState<Priority | null>(null);

  const { priorities, total, isLoading, isError, overview, users, teams } = usePriorityPageData(filters.params, debouncedUserSearch);

  const totalPages = Math.ceil(total / PRIORITY_ITEMS_PER_PAGE);
  const stats = {
    total: overview?.total ?? 0,
    totalFocus: overview?.totalFocus ?? 0,
    totalQueue: overview?.totalQueue ?? 0,
    completed: overview?.totalCompleted ?? 0,
  };

  // Creating a deliverable goes through the OneThing chat, not the form modal.
  const handleCreate = openChat;
  const handleView = (p: Priority) => { if (canAccessPriority(p, user?.pid, canViewOkr)) navigate(`/priorities/${p.pid}`); };
  const handleEdit = (p: Priority) => { if (canAccessPriority(p, user?.pid, canViewOkr)) { setEditingPriority(p); setIsDataDialogOpen(true); } };

  return (
    <PageShell>
      <PageHeaderFrame
        icon={Target}
        title="Deliverables"
        description={
          stats.total > 0 ? (
            <>
              <span className="font-medium text-foreground">{stats.total}</span> deliverables
              {stats.totalFocus > 0 && <> · <span className="font-medium text-foreground">{stats.totalFocus}</span> in focus</>}
              {stats.totalQueue > 0 && <> · <span className="font-medium text-foreground">{stats.totalQueue}</span> queued</>}
              <> · ranked by PPP</>
            </>
          ) : "Ranked by the PPP they deliver — link a PPP and a DRI to commit."
        }
        right={
          <Button variant="gradient" className="shrink-0 gap-2 rounded-full px-6 shadow-sm" onClick={handleCreate}>
            <Plus className="h-4 w-4" /> Add Deliverable
          </Button>
        }
      />

      <PriorityStats total={stats.total} totalFocus={stats.totalFocus} totalQueue={stats.totalQueue} completed={stats.completed} />

      <PriorityFilters
        search={filters.search} onSearchChange={filters.setSearch}
        typeFilter={filters.typeFilter} onTypeFilterChange={filters.setTypeFilter}
        statusFilter={filters.statusFilter} onStatusFilterChange={filters.setStatusFilter}
        userFilter={filters.userFilter} onUserFilterChange={filters.setUserFilter}
        teamFilter={filters.teamFilter} onTeamFilterChange={filters.setTeamFilter}
        pppFilter={filters.pppFilter} onPppFilterChange={filters.setPppFilter}
        candidatesOnly={filters.candidatesOnly} onCandidatesOnlyChange={filters.setCandidatesOnly}
        users={users} teams={teams}
        onUserSearchChange={setUserSearch}
        onClearAll={filters.clear}
        viewMode={filters.viewMode} onViewModeChange={filters.setViewMode}
      />

      <PriorityList
        priorities={priorities}
        isLoading={isLoading}
        isError={isError}
        onView={handleView}
        onEdit={handleEdit}
        onDelete={setPriorityToDelete}
        currentUser={user}
        isAdmin={isAdmin}
        hasActiveFilters={filters.hasActiveFilters}
        onClearFilters={filters.clear}
        onShowDone={isHidingDone(filters.statusFilter) ? () => filters.setStatusFilter([...filters.statusFilter, "done"]) : undefined}
        grouped={!filters.candidatesOnly}
      />

      <ModernPagination page={filters.page} totalPages={totalPages} onPageChange={filters.setPage} total={total} limit={PRIORITY_ITEMS_PER_PAGE} />

      {!filters.candidatesOnly && (
        <CandidateSection
          params={filters.params}
          onView={handleView} onEdit={handleEdit} onDelete={setPriorityToDelete} currentUser={user} isAdmin={isAdmin}
        />
      )}

      <PriorityDetailDialog open={isDataDialogOpen} onOpenChange={setIsDataDialogOpen} priority={editingPriority} />
      <PriorityDeleteDialog priority={priorityToDelete} onOpenChange={(open) => { if (!open) setPriorityToDelete(null); }} />
    </PageShell>
  );
};

export default Priorities;
